/**
 * ============================================================================
 * Hero Slideshow Script - Auto-Rotating Banner Controller
 * ============================================================================
 * 
 * This script powers the `.hero-slideshow` element by handling slide transitions.
 * It waits for the DOM to load, then:
 * 
 * 1. Locates every `.hero-slideshow` container.
 * 2. Grabs each container's `.hero-slide` children.
 * 3. Uses `data-speed` (in seconds, defaults to 5) to determine interval timing.
 * 4. Activates the first slide immediately via `showSlide()`.
 * 5. If `data-auto="true"` is present on the container, it:
 *    - Advances the slide every `speed` milliseconds using `setInterval`.
 *    - Loops back to the first slide after the last one.
 * 
 * Slide transitions are handled by toggling the `active` class on the slides.
 * Only one slide will have `.active` at any time.
 * 
 * Usage Notes:
 * - Add `data-auto="true"` and `data-speed="6"` to `.hero-slideshow` to enable rotation.
 * - Ensure CSS includes rules for `.hero-slide` and `.hero-slide.active` visibility.
 * - Autoplay is disabled when reduced motion is requested.
 * - Shopify Theme Editor section reloads are initialized automatically.
 * - `data-hero-slideshow-initialized` prevents duplicate timers.
 * 
 * ============================================================================
 */

(() => {
  const initializeSlideshow = (slider) => {
    if (slider.dataset.heroSlideshowInitialized === 'true') return;

    const slides = [...slider.querySelectorAll('.hero-slide')];
    if (slides.length === 0) return;

    slider.dataset.heroSlideshowInitialized = 'true';
    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('active')));
    const configuredSeconds = Number.parseInt(slider.dataset.speed || '5', 10);
    const speed = Math.max(1, Number.isFinite(configuredSeconds) ? configuredSeconds : 5) * 1000;

    const showSlide = (index) => {
      slides.forEach((slide, slideIndex) => slide.classList.toggle('active', slideIndex === index));
    };

    showSlide(current);

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (slider.dataset.auto === 'true' && slides.length > 1 && !reducedMotion) {
      const timer = window.setInterval(() => {
        if (!slider.isConnected) {
          window.clearInterval(timer);
          return;
        }
        current = (current + 1) % slides.length;
        showSlide(current);
      }, speed);
    }
  };

  const initializeWithin = (root) => {
    if (root.matches?.('.hero-slideshow')) initializeSlideshow(root);
    root.querySelectorAll?.('.hero-slideshow').forEach(initializeSlideshow);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  } else {
    initializeWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
